import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSessionStore } from '@/state/sessionStore';
import { useSimulationStore } from '@/state/simulationStore';
import { aud, customerName, simTimeShort } from '@/utils/format';
import { bookingPriceAud } from '@/domain/pricing';
import { recommendForClient } from '@/domain/cashFlow';
import { checkInBooking } from '@/domain/sessionFlow';
import { ensureConfirmationCall } from '@/domain/confirmationFlow';
import { isRunningLate, lateMinutes } from '@/domain/lateness';
import { isCommitted } from '@/domain/payoutFlow';
import { AWAITING_CLIENT } from '@/domain/selfBookFlow';
import { bookingStatusTone } from '@/utils/statusTone';
import ConfirmationWorkModal from '@/components/confirmations/ConfirmationWorkModal';
import {
  type Activity,
  AlertTriangle,
  Armchair,
  Banknote,
  ChevronRight,
  Clock,
  Phone,
  Radio,
  Search,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';
import { isSameDay } from 'date-fns';
import clsx from 'clsx';
import type { Booking, CustomerRecord, StudioId } from '@/domain/types';

type Bucket = 'arriving' | 'checked-in' | 'in-chair' | 'completed';

function bucketOf(booking: Booking): Bucket {
  if (booking.status === 'in-chair') return 'in-chair';
  if (booking.status === 'checked-in') return 'checked-in';
  if (booking.status === 'completed') return 'completed';
  return 'arriving';
}

// Order the columns render in, plus per-bucket heading / empty copy / row accent.
const BUCKET_ORDER: Bucket[] = ['in-chair', 'checked-in', 'arriving', 'completed'];

const BUCKET_META: Record<Bucket, { heading: string; empty: string; rowBorder: string }> = {
  'in-chair': {
    heading: 'In chair · live',
    empty: 'No one in chair right now.',
    rowBorder: 'border-violet/40 bg-violet/5 hover:border-violet/70',
  },
  'checked-in': {
    heading: 'Checked in · seat them',
    empty: 'No one waiting to be seated.',
    rowBorder: 'border-azure/40 bg-azure/5 hover:border-azure',
  },
  arriving: {
    heading: 'Arriving · check them in',
    empty: 'No one expected to walk in. Empty calendar today.',
    rowBorder: 'border-gold-bright/40 hover:border-gold-bright',
  },
  completed: {
    heading: 'Completed · checked out',
    empty: 'No completed bookings yet today.',
    rowBorder: 'border-mint/30 bg-mint/5 hover:border-mint',
  },
};

export default function FloorManager() {
  const navigate = useNavigate();
  const bookings = useBrainStore((s) => s.bookings);
  const customers = useBrainStore((s) => s.customers);
  const artists = useBrainStore((s) => s.artists);
  const studios = useBrainStore((s) => s.studios);
  const pieces = useBrainStore((s) => s.pieces);
  const waivers = useBrainStore((s) => s.waivers);
  const salePayments = useBrainStore((s) => s.salePayments);
  const clockIso = useSimulationStore((s) => s.clockIso);
  const assignedStudios = useSessionStore((s) => s.assignedStudios);
  const persona = useSessionStore((s) => s.persona);
  const pushNotification = useUIStore((s) => s.pushNotification);

  const waiverSignedFor = (bookingId: string) =>
    Object.values(waivers).some((w) => w.bookingId === bookingId && w.status === 'signed');

  // Customer-less rows: a self-book held/pending slot is stamped with
  // AWAITING_CLIENT until a client claims the link. Show a placeholder rather
  // than the raw sentinel id.
  const rowName = (customer: CustomerRecord | undefined, booking: Booking) =>
    customer
      ? customerName(customer)
      : booking.customerId === AWAITING_CLIENT
        ? 'Awaiting client'
        : booking.customerId;

  // Step 1 inline from the desk. The domain function is the gate; if it refuses
  // (waiver unsigned) we route the manager into the booking to resolve it there.
  const onCheckIn = (booking: Booking, customer: CustomerRecord | undefined) => {
    const result = checkInBooking(booking.id);
    if (!result.ok) {
      if (result.reason === 'waiver-unsigned') {
        pushNotification({
          intensity: 'high',
          title: 'Waiver not signed',
          body: `${rowName(customer, booking)} needs to sign the medical waiver before check-in.`,
          ttlMs: 4000,
        });
        navigate(`/floor/${booking.id}`);
      }
      return;
    }
    pushNotification({
      intensity: 'high',
      title: 'Checked in',
      body: `${rowName(customer, booking)} is checked in — seat them when a chair's ready.`,
      ttlMs: 4000,
    });
  };

  // Late-arrival Contact flow: the confirmation work-modal (late-arrival mode)
  // opened for a running-late row, keyed by the confirmation call it mints.
  const [contactCallId, setContactCallId] = useState<string | null>(null);
  const onContact = (booking: Booking) => {
    const callId = ensureConfirmationCall(booking.id);
    if (callId) setContactCallId(callId);
  };

  const allowAll = persona === 'georgina' || persona === 'owner';
  const [studioFilter, setStudioFilter] = useState<StudioId | 'all'>(
    allowAll ? 'all' : (assignedStudios[0] ?? 'all'),
  );
  const [query, setQuery] = useState('');

  // Force re-render every minute so the timers tick.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    const today = new Date(clockIso);
    return Object.values(bookings)
      .filter((b) => isSameDay(new Date(b.start), today))
      // The floor shows only committed appointments (confirmed / checked-in /
      // in-chair / completed). Held / pending-deposit / rescheduled / cancelled /
      // no-show / superseded bookings are calendar and approvals states, not the
      // floor, so they should not clutter the arriving bucket.
      .filter((b) => isCommitted(b))
      .filter((b) =>
        studioFilter === 'all' ? true : b.studioId === studioFilter,
      )
      .filter((b) =>
        allowAll || assignedStudios.length === 0
          ? true
          : assignedStudios.includes(b.studioId),
      )
      .filter((b) => {
        // Free-text search across who's on the floor: customer, artist, or brief.
        if (!q) return true;
        const cust = customers[b.customerId];
        const artist = artists[b.artistId];
        const haystack = [
          cust ? customerName(cust) : b.customerId,
          artist?.displayName ?? b.artistId,
          b.designBrief,
        ]
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  }, [bookings, clockIso, studioFilter, allowAll, assignedStudios, q, customers, artists]);

  // A running-late arrival: 15+ min past its start, still in the arriving bucket
  // (not yet checked in), and tied to a real client we can actually contact
  // (self-book AWAITING_CLIENT slots have no one to chase).
  const isLateArrival = (b: Booking) =>
    !!customers[b.customerId] && isRunningLate(b.start, clockIso);

  const grouped: Record<Bucket, Booking[]> = {
    arriving: [],
    'checked-in': [],
    'in-chair': [],
    completed: [],
  };
  for (const b of filtered) grouped[bucketOf(b)].push(b);

  // Late arrivals float to the top of the arriving queue (most overdue first),
  // keeping the start-ascending order within the late and on-time groups.
  grouped.arriving = [
    ...grouped.arriving.filter(isLateArrival),
    ...grouped.arriving.filter((b) => !isLateArrival(b)),
  ];

  const inChairCount = grouped['in-chair'].length;
  const lateCount = grouped.arriving.filter(isLateArrival).length;

  return (
    <section className="px-8 py-8" data-testid="floor-manager">
      <header className="flex items-end justify-between max-w-7xl flex-wrap gap-4">
        <div>
          <p className="label-small-caps text-gold-bright">Floor</p>
          <h1 className="heading-display text-3xl text-cream mt-2">On the floor today</h1>
          <p className="font-serif text-cream-soft mt-3 max-w-2xl">
            Check customers in, watch the chairs run live, and run the check-out flow that locks in
            their next session. Designed for the iPad on the desk.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search
              size={13}
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-cream-faint"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, artist, brief…"
              aria-label="Search the floor"
              className="w-56 bg-ink-deep border border-gold-dim/40 rounded-md pl-8 pr-3 py-1.5 text-sm text-cream placeholder:text-cream-faint focus:outline-none focus:border-gold-bright"
              data-testid="floor-search"
            />
          </div>
          <label className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
            Studio
          </label>
          <select
            value={studioFilter}
            onChange={(e) => setStudioFilter(e.target.value as StudioId | 'all')}
            className="bg-ink-deep border border-gold-dim/40 rounded-md px-3 py-1.5 text-sm text-cream"
            data-testid="floor-studio-filter"
          >
            <option value="all">All studios</option>
            {Object.values(studios).map((s) => (
              <option key={s.id} value={s.id}>
                {s.brand} · {s.name}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-w-7xl">
        <KpiTile
          label="Arriving"
          value={grouped.arriving.length}
          icon={Clock}
          tone="warn"
          note={lateCount > 0 ? `${lateCount} running late` : undefined}
        />
        <KpiTile label="Checked in" value={grouped['checked-in'].length} icon={ShieldAlert} tone="info" />
        <KpiTile label="In chair" value={inChairCount} icon={Radio} tone="live" />
        <KpiTile label="Completed" value={grouped.completed.length} icon={Sparkles} tone="good" />
      </div>

      <div className="mt-6 grid grid-cols-12 gap-6 max-w-7xl">
        {BUCKET_ORDER.map((bucket) => (
          <Fragment key={bucket}>
            <section
              className={clsx(
                'col-span-12',
                // In-chair (live) and completed (the day's log) run full-width;
                // the two pre-chair queues — checked-in + arriving — sit
                // side-by-side beneath, wide enough to keep names readable.
                bucket === 'in-chair' || bucket === 'completed'
                  ? 'lg:col-span-12'
                  : 'lg:col-span-6',
              )}
              data-testid={`floor-bucket-${bucket}`}
            >
              <h2 className="label-small-caps text-gold-bright mb-3">
                {BUCKET_META[bucket].heading}
                <span className="ml-2 font-mono text-[10px] text-cream-faint">
                  {grouped[bucket].length}
                </span>
              </h2>
              <ul className="space-y-2">
                {grouped[bucket].length === 0 ? (
                  <li className="panel-dark p-4 font-serif text-cream-soft">
                    {q ? `No matches for "${query.trim()}".` : BUCKET_META[bucket].empty}
                  </li>
                ) : null}
                {grouped[bucket].map((b) => {
                  const cust = customers[b.customerId];
                  const artist = artists[b.artistId];
                  const piece = b.pieceId ? pieces[b.pieceId] : undefined;
                  const seatedIso = b.seatedAt ?? b.checkedInAt;
                  const elapsed =
                    b.status === 'in-chair' && seatedIso
                      ? Math.max(
                          0,
                          Math.floor(
                            (new Date(clockIso).getTime() - new Date(seatedIso).getTime()) / 60_000,
                          ),
                        )
                      : null;
                  const price = bookingPriceAud(b, artist);
                  const waiverSigned = waiverSignedFor(b.id);
                  const late = bucket === 'arriving' && isLateArrival(b);
                  const lateBy = late ? lateMinutes(b.start, clockIso) : 0;
                  // Per-client cash steer (Stage 2): the studio front-loads cash
                  // onto its earliest unpaid clients to cover the day's committed
                  // payouts, so each arriving/checked-in/in-chair client gets its
                  // own call rather than one flag for the whole studio. BOTH calls
                  // now show explicitly - cash keeps the alert blood chip, card
                  // gets a calm mint "Card is fine" chip - so the desk always sees
                  // a steer instead of card clients showing nothing. Completed
                  // clients have already paid, so they get the paid-method chip
                  // below (Part B) instead of a forward-looking steer.
                  const paymentSteer =
                    bucket === 'completed' ? null : recommendForClient(b.studioId, clockIso, b.id);
                  const steerChip =
                    paymentSteer === 'cash' ? (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-blood px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-ink-black shadow-sm"
                        data-testid={`floor-row-ask-cash-${b.id}`}
                      >
                        <Banknote size={11} /> Ask for cash
                      </span>
                    ) : paymentSteer === 'card' ? (
                      <span
                        className="pill-mint text-[10px]"
                        data-testid={`floor-row-card-fine-${b.id}`}
                      >
                        <Banknote size={11} /> Card is fine
                      </span>
                    ) : null;
                  // Paid-method chip (Part B): a completed client has already
                  // paid, so tell the desk HOW rather than steering them forward.
                  // Neutral/muted cream tone - informational, not a call to
                  // action - and absent when the booking has no sale record.
                  const sale = bucket === 'completed' ? salePayments[`sale-${b.id}`] : undefined;
                  const paidChip = sale ? (
                    <span className="pill-cream text-[10px]" data-testid={`floor-row-paid-method-${b.id}`}>
                      {sale.method === 'cash'
                        ? 'Paid cash'
                        : sale.method === 'card'
                          ? 'Paid card'
                          : sale.method === 'mixed'
                            ? 'Paid mixed'
                            : 'Paid via deposit'}
                    </span>
                  ) : null;
                  return (
                    <li key={b.id} data-testid={`floor-row-${b.id}`}>
                      <div
                        className={clsx(
                          'w-full panel-dark p-4 transition border flex items-center gap-4',
                          BUCKET_META[bucket].rowBorder,
                          // Running late: bold orange glow (thick border + tinted
                          // fill + pulsing ring) so the desk catches it instantly
                          // on the light floor, overriding the bucket accent.
                          late && '!border-2 !border-orange bg-orange/10 animate-ring-pulse-orange',
                        )}
                        data-testid={late ? `floor-late-${b.id}` : undefined}
                      >
                        <button
                          type="button"
                          onClick={() => navigate(`/floor/${b.id}`)}
                          className="flex items-center gap-4 flex-1 min-w-0 text-left"
                          data-testid={`floor-row-open-${b.id}`}
                        >
                          <div className="shrink-0 w-12 text-center">
                            <p className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                              {simTimeShort(b.start)}
                            </p>
                            {bucket === 'in-chair' && elapsed != null ? (
                              <p className="font-inter text-base text-blood mt-1">
                                {Math.floor(elapsed / 60)}h{(elapsed % 60).toString().padStart(2, '0')}
                              </p>
                            ) : null}
                          </div>
                          {b.referencePhotos.length > 0 ? (
                            <img
                              src={b.referencePhotos[0]!}
                              alt=""
                              loading="lazy"
                              className="h-9 w-9 shrink-0 rounded-sm object-cover border border-gold-dim/30"
                              data-testid={`floor-row-thumb-${b.id}`}
                            />
                          ) : null}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-inter text-base text-cream truncate">
                                {rowName(cust, b)}
                              </p>
                              <span
                                className={clsx(
                                  'px-1.5 py-0.5 rounded-sm font-inter text-[9px] uppercase tracking-widest inline-flex items-center gap-1',
                                  bookingStatusTone(b.status).fill,
                                  bookingStatusTone(b.status).text,
                                )}
                              >
                                <span
                                  className={clsx(
                                    'h-1.5 w-1.5 rounded-full',
                                    bookingStatusTone(b.status).dot,
                                  )}
                                />
                                {bookingStatusTone(b.status).label}
                              </span>
                              {piece ? (
                                <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">
                                  {(piece.sessionsCompleted ?? 0) + (b.status === 'in-chair' ? 1 : 0)} of {piece.estimatedTotalSessions ?? '?'}
                                </span>
                              ) : null}
                            </div>
                            <p className="font-serif text-sm text-cream-soft mt-1 truncate">
                              {artist?.displayName ?? b.artistId} · {b.sessionLength.replace('-', ' ')} · {aud(price)} · {b.designBrief}
                            </p>
                            {late ? (
                              <p
                                className="font-inter text-[10px] text-orange font-semibold uppercase tracking-widest mt-1 inline-flex items-center gap-1"
                                data-testid={`floor-late-line-${b.id}`}
                              >
                                <AlertTriangle size={11} /> {lateBy} min late · contact them
                              </p>
                            ) : null}
                            {bucket === 'in-chair' && b.boothNumber ? (
                              <p className="font-inter text-[10px] text-blood uppercase tracking-widest mt-1">
                                Booth {b.boothNumber} · recording
                              </p>
                            ) : null}
                            {bucket === 'checked-in' ? (
                              <p className="font-inter text-[10px] text-azure uppercase tracking-widest mt-1">
                                Waiting for a chair
                              </p>
                            ) : null}
                            {bucket === 'completed' && b.amountPaidAud ? (
                              <p className="font-inter text-[10px] text-mint uppercase tracking-widest mt-1">
                                Paid {aud(b.amountPaidAud)}{b.rolledDepositId ? ' · deposit rolled' : ''}
                              </p>
                            ) : null}
                          </div>
                        </button>

                        {/* Step actions — check in (step 1) then seat (step 2).
                            On arriving rows the studio's payment steer sits
                            directly above the action so the desk catches it at
                            the moment of check-in — a solid blood-red chip
                            (`text-ink-black` is the near-white text token in
                            light theme) for cash, a calm mint pill for card. */}
                        {bucket === 'arriving' ? (
                          <div className="flex flex-col items-center gap-1.5 shrink-0">
                            {late ? (
                              // A running-late arrival: the lateness IS the steer,
                              // so a bold orange "X min late" banner takes the slot
                              // the steer chip would otherwise occupy.
                              <span
                                className="inline-flex items-center gap-1 rounded-full bg-orange px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-ink-black shadow-sm"
                                data-testid={`floor-row-late-banner-${b.id}`}
                              >
                                <AlertTriangle size={11} /> {lateBy} min late
                              </span>
                            ) : (
                              steerChip
                            )}
                            {late ? (
                              // They haven't arrived yet - chase them before check-in.
                              <button
                                type="button"
                                onClick={() => onContact(b)}
                                className="btn-primary text-xs"
                                data-testid={`floor-contact-${b.id}`}
                              >
                                <Phone size={12} /> Contact
                              </button>
                            ) : waiverSigned ? (
                              <button
                                type="button"
                                onClick={() => onCheckIn(b, cust)}
                                className="btn-primary text-xs"
                                data-testid={`floor-check-in-${b.id}`}
                              >
                                <Radio size={12} /> Check in
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => navigate(`/floor/${b.id}`)}
                                className="inline-flex items-center gap-1 rounded-md border border-blood/50 bg-blood/10 px-2.5 py-1.5 text-[11px] uppercase tracking-widest text-blood hover:bg-blood/20 transition"
                                data-testid={`floor-waiver-blocked-${b.id}`}
                                title="Waiver not signed — resolve before check-in"
                              >
                                <ShieldAlert size={12} /> Waiver
                              </button>
                            )}
                          </div>
                        ) : bucket === 'checked-in' ? (
                          <div className="flex flex-col items-center gap-1.5 shrink-0">
                            {steerChip}
                            <button
                              type="button"
                              onClick={() => navigate(`/floor/${b.id}`)}
                              className="btn-primary text-xs shrink-0"
                              data-testid={`floor-seat-${b.id}`}
                            >
                              <Armchair size={12} /> Place in chair
                            </button>
                          </div>
                        ) : bucket === 'in-chair' ? (
                          steerChip
                        ) : bucket === 'completed' ? (
                          paidChip
                        ) : null}

                        <ChevronRight size={16} className="text-cream-faint shrink-0" />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          </Fragment>
        ))}
      </div>

      {contactCallId ? (
        <ConfirmationWorkModal
          callId={contactCallId}
          onClose={() => setContactCallId(null)}
          lateArrival
        />
      ) : null}
    </section>
  );
}

function KpiTile({
  label,
  value,
  icon: Icon,
  tone,
  note,
}: {
  label: string;
  value: number | string;
  icon: typeof Activity;
  tone: 'critical' | 'warn' | 'info' | 'good' | 'live' | 'neutral';
  /** Optional emphasis line under the value (e.g. "2 running late"). */
  note?: string;
}) {
  return (
    <div
      className={clsx(
        'panel-dark p-3 border',
        tone === 'critical'
          ? 'border-blood/40'
          : tone === 'warn'
            ? 'border-gold-bright/40'
            : tone === 'info'
              ? 'border-azure/40'
              : tone === 'good'
                ? 'border-mint/40'
                : tone === 'live'
                  ? 'border-violet/40'
                  : 'border-gold-dim/20',
      )}
    >
      <p
        className={clsx(
          'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-inter uppercase tracking-widest',
          tone === 'critical'
            ? 'bg-blood/20 text-blood'
            : tone === 'warn'
              ? 'bg-gold/20 text-gold-bright'
              : tone === 'info'
                ? 'bg-azure/20 text-azure'
                : tone === 'good'
                  ? 'bg-mint/20 text-mint'
                  : tone === 'live'
                    ? 'bg-violet/20 text-violet'
                    : 'bg-cream-soft/10 text-cream-soft',
        )}
      >
        <Icon size={9} /> {label}
      </p>
      <p className="font-cinzel text-2xl text-cream mt-2">{value}</p>
      {note ? (
        <p className="inline-flex items-center gap-1 mt-1 font-inter text-[10px] uppercase tracking-widest text-gold-bright">
          <AlertTriangle size={10} /> {note}
        </p>
      ) : null}
    </div>
  );
}
